import { useEffect, useState } from 'react';
import {
  Avatar,
  Breadcrumb,
  Button,
  Divider,
  Dropdown,
  Input,
  Layout,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  BellOutlined,
  DownOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { API_BASE_URL, getCurrentUserProfile } from '../../services/api';
import { trackEvent } from '../../services/telemetry';

const { Header: AntHeader } = Layout;

const pageNames = {
  '/': 'Dashboard',
  '/verification': 'Verification',
  '/users': 'Users',
  '/settings': 'Settings',
};

const adminLevelLabels = {
  0: 'Read-only',
  1: 'Operational',
  2: 'Super admin',
};

/**
 * Top header: role badge, signed-in user, and sign-out.
 */
export default function Header() {
  const location = useLocation();
  const { profile, logout } = useAuth();
  const { role, adminLevel } = usePermissions();
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const isProductionEnvironment = window.location.hostname === 'admin.journiywellness.com';
  const environmentLabel = isProductionEnvironment ? 'Production' : 'BVT';

  useEffect(() => {
    if (!profile?.id || import.meta.env.VITE_DEV_BYPASS_AUTH === 'true') return undefined;

    let active = true;
    setProfileLoading(true);
    getCurrentUserProfile(profile.id)
      .then((details) => {
        if (active) setUserProfile(details);
      })
      .catch(() => {
        if (active) setUserProfile(null);
      })
      .finally(() => {
        if (active) setProfileLoading(false);
      });

    return () => {
      active = false;
    };
  }, [profile?.id]);

  const name = userProfile?.first_name || profile?.firstName || profile?.name || 'Admin user';
  const email = userProfile?.email || profile?.email || 'Email unavailable';
  const imageUrl = userProfile?.image_url || userProfile?.image || profile?.picture;
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'ME';
  const currentPage = pageNames[location.pathname] || 'Dashboard';
  const adminLevelLabel = adminLevelLabels[adminLevel] || adminLevelLabels[0];

  const handleLogout = () => {
    trackEvent('admin_logout', { email: email || undefined });
    logout();
  };

  const menuItems = [{
    key: 'logout',
    label: 'Sign out',
    icon: <LogoutOutlined />,
    onClick: handleLogout,
  }];

  const accountPanel = (
    <div className="admin-account-panel">
      <div className="admin-account-profile">
        <Avatar
          className="admin-account-avatar"
          size={64}
          src={imageUrl}
          icon={!imageUrl && <UserOutlined />}
        >
          {!imageUrl && initials}
        </Avatar>
        <div className="admin-account-summary">
          <Typography.Text className="admin-account-name" ellipsis={{ tooltip: name }}>
            {name}
          </Typography.Text>
          <Typography.Text className="admin-account-email" ellipsis={{ tooltip: email }}>
            {email}
          </Typography.Text>
          <Tag className="admin-account-role" color={role === 'admin' ? 'green' : 'blue'}>
            {role === 'anonymous' ? 'Signed in' : role}
          </Tag>
          {role === 'admin' && (
            <Tag className="admin-account-level" color={adminLevel === 2 ? 'red' : adminLevel === 1 ? 'gold' : 'default'}>
              Level {adminLevel}: {adminLevelLabel}
            </Tag>
          )}
        </div>
      </div>
      <Divider />
      {profileLoading && (
        <div className="admin-account-loading"><Spin size="small" /> Loading profile</div>
      )}
      <div className="admin-account-menu">{menuItems.map((item) => (
        <button className="admin-account-signout" type="button" key={item.key} onClick={handleLogout}>
          {item.icon}
          <span>{item.label}</span>
        </button>
      ))}</div>
    </div>
  );

  return (
    <AntHeader
      className={`admin-header ${isProductionEnvironment ? 'admin-header--production' : 'admin-header--bvt'}`}
      style={{
        background: 'var(--admin-bg-header)',
      }}
    >
      <Breadcrumb
        items={[
          { title: 'Journiy' },
          { title: currentPage },
        ]}
      />
      <span
        className={`admin-environment-pill ${
          isProductionEnvironment ? 'admin-environment-pill--production' : 'admin-environment-pill--bvt'
        }`}
      >
        Environment: {environmentLabel}
      </span>
      <Input className="admin-search" prefix={<SearchOutlined />} placeholder="Search" />
      <Space size={4} className="admin-header-actions">
        <Tooltip title="Notifications"><Button type="text" icon={<BellOutlined />} /></Tooltip>
        <Tooltip title="Help"><Button type="text" icon={<QuestionCircleOutlined />} /></Tooltip>
        <Dropdown popupRender={() => accountPanel} placement="bottomRight" trigger={['click']}>
          <Button
            className="admin-me-control"
            type="text"
            aria-label={`Open menu for ${name}`}
            title={name}
          >
            <Avatar size={22} src={imageUrl} icon={!imageUrl && <UserOutlined />}>
              {!imageUrl && initials}
            </Avatar>
            <span>ME</span>
            <DownOutlined className="admin-me-chevron" />
          </Button>
        </Dropdown>
      </Space>
    </AntHeader>
  );
}
